import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { AlarmClock, Play } from "lucide-react";

// Lot 770 — « Alerte ticket oublié » : le membre attend depuis ≥ N h → cloche + e-mail au responsable (sinon gestionnaires
// du support + super admins), puis toutes les 24 h ; super admins en copie dès le 2e rappel. Réglage super admin.
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "");

// Pastille sur un ticket déjà relancé : « ⏰ Relancé ×2 · 01/11 15:00 ».
export const StaleAlertChip = ({ ticket, testId = "ticket-stale-alert-chip" }) => {
  const s = ticket?.stale_last;
  if (!s || ticket?.status !== "open") return null;
  return (
    <span className="chip text-[10px] inline-flex items-center gap-1" style={{ background: "#FFF7ED", color: "#9A3412", borderColor: "#FDBA74" }} title={`Rappel n° ${s.n} envoyé le ${fmt(s.at)} (${s.hours} h sans réponse)${s.cc?.length ? " — super admins en copie" : ""}`} data-testid={testId} data-n={s.n}>
      <AlarmClock size={10} /> Relancé ×{s.n} · {fmt(s.at)}
    </span>
  );
};

export const StaleAlertsCard = ({ isSuper, onRan }) => {
  const [cfg, setCfg] = useState(null);
  const [hours, setHours] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/tickets/stale-config").then(({ data }) => { setCfg(data); setHours(String(data.hours)); }).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!cfg) return null;
  const save = async (patch) => {
    setBusy(true);
    try { const { data } = await api.patch("/admin/tickets/stale-config", patch); setCfg(data); setHours(String(data.hours)); toast.success("Réglage enregistré"); }
    catch (e) { toast.error(e?.friendlyMessage || e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };
  const runNow = async () => {
    setBusy(true);
    try { const { data } = await api.post("/admin/tickets/stale-run"); toast.success(data.disabled ? "Alerte désactivée : rien envoyé" : `${data.reminded} ticket(s) relancé(s) sur ${data.checked} ouvert(s)`); load(); onRan?.(); }
    catch (e) { toast.error(e?.friendlyMessage || "Erreur"); }
    finally { setBusy(false); }
  };
  const h = parseInt(hours, 10);
  const lr = cfg.last_run;
  return (
    <div className="yalola-card p-4 space-y-2" data-testid="stale-alerts-card" data-enabled={cfg.enabled ? "1" : "0"}>
      <div className="flex items-center gap-2 flex-wrap">
        <AlarmClock size={15} style={{ color: cfg.enabled ? "#C2410C" : "var(--yalola-text-muted)" }} />
        <b className="text-sm">Alerte ticket oublié</b>
        <span className="chip text-[10px]" style={cfg.enabled ? { background: "#FFEDD5", color: "#9A3412" } : {}} data-testid="stale-alerts-state">{cfg.enabled ? `Active · après ${cfg.hours} h` : "Désactivée"}</span>
        {isSuper && <Switch checked={cfg.enabled} onCheckedChange={(v) => save({ enabled: v })} disabled={busy} aria-label="Activer l'alerte ticket oublié" data-testid="stale-alerts-toggle" />}
        {isSuper && (
          <Button size="sm" variant="outline" className="rounded-full h-8 ml-auto" onClick={runNow} disabled={busy} data-testid="stale-alerts-run">
            <Play size={12} className="mr-1" /> Lancer maintenant
          </Button>
        )}
      </div>
      <p className="text-xs" style={{ color: "var(--yalola-text-muted)" }} data-testid="stale-alerts-help">
        Quand un membre attend une réponse depuis plus de <b>{cfg.hours} h</b>, l'agent responsable reçoit une cloche{cfg.email ? " + un e-mail" : ""}, puis une relance toutes les {cfg.repeat_hours || 24} h tant que le membre attend (super admins en copie dès la 2e). Sans responsable : gestionnaires du support + super admins.
      </p>
      {isSuper && (
        <div className="flex items-center gap-2 flex-wrap text-xs" data-testid="stale-alerts-settings">
          <span>Prévenir après</span>
          <Input type="number" min={1} max={720} value={hours} onChange={(e) => setHours(e.target.value)} className="w-20 h-8" data-testid="stale-alerts-hours" />
          <span>h</span>
          <Button size="sm" className="pill-primary rounded-full h-8" disabled={busy || !(h >= 1 && h <= 720) || h === cfg.hours} onClick={() => save({ hours: h })} data-testid="stale-alerts-hours-save">Enregistrer</Button>
          <label className="inline-flex items-center gap-1.5 ml-2 cursor-pointer">
            <Switch checked={cfg.email} onCheckedChange={(v) => save({ email: v })} disabled={busy} aria-label="Envoyer aussi un e-mail" data-testid="stale-alerts-email" />
            <span>E-mail en plus de la cloche</span>
          </label>
        </div>
      )}
      {lr?.at && (
        <div className="text-[11px]" style={{ color: "var(--yalola-text-muted)" }} data-testid="stale-alerts-last-run">
          Dernier passage : {fmt(lr.at)} — {lr.disabled ? "alerte désactivée" : `${lr.reminded} relance(s) sur ${lr.checked} ticket(s) ouvert(s)${lr.emailed ? `, ${lr.emailed} e-mail(s)` : ""}`}.
        </div>
      )}
    </div>
  );
};
